import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Brain, Target, TrendingUp, TrendingDown, Clock,
  BookOpen, Award, AlertCircle, CheckCircle2, Lightbulb,
  Calendar, BarChart2, Zap, RefreshCw, ChevronRight,
  ArrowUpRight, ArrowDownRight, BookMarked, GraduationCap
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LearningInsights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const token = localStorage.getItem('faheem_token');

  useEffect(() => {
    if (token) {
      loadInsights();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/learning-insights/analyze?access_token=${token}`
      );
      if (response.data) {
        setInsights(response.data);
      }
    } catch (error) {
      console.error('Failed to load insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateStudyPlan = async () => {
    try {
      setGenerating(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/learning-insights/generate-plan?access_token=${token}`
      );
      if (response.data) {
        setInsights(prev => ({ ...prev, study_plan: response.data.study_plan }));
      }
    } catch (error) {
      console.error('Failed to generate study plan:', error);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="learning-insights-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-500">Analyzing your learning patterns...</p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <Card className="bg-white border-slate-200" data-testid="learning-insights-empty">
        <CardContent className="py-16 text-center">
          <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Learning Data Yet</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            Start taking quizzes and completing assignments to get personalized learning insights from FaheemGPT.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="learning-insights-container">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-0 shadow-xl overflow-hidden" data-testid="learning-insights-header">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-yellow-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">FaheemGPT Learning Insights</h2>
                <p className="text-slate-400">AI-powered analysis of your learning journey</p>
              </div>
            </div>
            <Button 
              onClick={loadInsights} 
              variant="outline" 
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="refresh-insights-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6" data-testid="insights-quick-stats">
            <div className="bg-white/10 rounded-xl p-4 text-center" data-testid="overall-score-stat">
              <BarChart2 className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{insights.overall_score || 0}%</p>
              <p className="text-xs text-slate-400">Overall Score</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center" data-testid="strength-areas-stat">
              <Target className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{insights.strengths?.length || 0}</p>
              <p className="text-xs text-slate-400">Strength Areas</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center" data-testid="focus-areas-stat">
              <AlertCircle className="w-6 h-6 text-orange-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{insights.weaknesses?.length || 0}</p>
              <p className="text-xs text-slate-400">Focus Areas</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center" data-testid="study-time-stat">
              <Clock className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{insights.total_study_hours || 0}h</p>
              <p className="text-xs text-slate-400">Study Time</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Strength Areas */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4 text-green-600" />
              </div>
              Strength Areas
            </CardTitle>
            <CardDescription>Topics you've mastered</CardDescription>
          </CardHeader>
          <CardContent>
            {insights.strengths && insights.strengths.length > 0 ? (
              <div className="space-y-3">
                {insights.strengths.map((strength, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-slate-900">{strength.topic_name}</p>
                        <p className="text-xs text-slate-500">{strength.course_name}</p>
                      </div>
                    </div>
                    <Badge className="bg-green-600 text-white">{strength.score}%</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-6">Complete more quizzes to identify your strengths</p>
            )}
          </CardContent>
        </Card>

        {/* Weak Areas */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-orange-600" />
              </div>
              Focus Areas
            </CardTitle>
            <CardDescription>Topics that need more attention</CardDescription>
          </CardHeader>
          <CardContent>
            {insights.weaknesses && insights.weaknesses.length > 0 ? (
              <div className="space-y-3">
                {insights.weaknesses.map((weakness, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-xl border border-orange-100">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="font-medium text-slate-900">{weakness.topic_name}</p>
                        <p className="text-xs text-slate-500">{weakness.course_name}</p>
                      </div>
                    </div>
                    <Badge className="bg-orange-600 text-white">{weakness.score}%</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-6">Great job! No weak areas identified yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recommended Focus Topics */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <BookMarked className="w-4 h-4 text-blue-600" />
            </div>
            Recommended Focus Topics
          </CardTitle>
          <CardDescription>FaheemGPT suggests focusing on these topics next</CardDescription>
        </CardHeader>
        <CardContent>
          {insights.recommended_topics && insights.recommended_topics.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {insights.recommended_topics.map((topic, idx) => (
                <div key={idx} className="p-4 bg-blue-50 rounded-xl border border-blue-100 hover:shadow-md transition-all">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{topic.topic_name}</p>
                      <p className="text-xs text-slate-500 mt-1">{topic.reason}</p>
                      <Badge variant="outline" className="mt-2 text-xs">{topic.priority}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-6">Complete more activities to get personalized recommendations</p>
          )}
        </CardContent>
      </Card>

      {/* Study Habits Analysis */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Clock className="w-4 h-4 text-purple-600" />
            </div>
            Study Habits Analysis
          </CardTitle>
          <CardDescription>Insights into your learning patterns</CardDescription>
        </CardHeader>
        <CardContent>
          {insights.study_habits ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-slate-600" />
                  <h4 className="font-semibold text-slate-900">Best Study Days</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {insights.study_habits.best_days?.map((day, idx) => (
                    <Badge key={idx} className="bg-slate-900 text-white">{day}</Badge>
                  )) || <span className="text-slate-500 text-sm">Not enough data</span>}
                </div>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-slate-600" />
                  <h4 className="font-semibold text-slate-900">Peak Study Hours</h4>
                </div>
                <p className="text-slate-700">{insights.study_habits.peak_hours || 'Not enough data'}</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-slate-600" />
                  <h4 className="font-semibold text-slate-900">Average Session</h4>
                </div>
                <p className="text-slate-700">{insights.study_habits.avg_session_duration || 'N/A'} minutes</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-slate-600" />
                  <h4 className="font-semibold text-slate-900">Study Streak</h4>
                </div>
                <p className="text-slate-700">{insights.study_habits.current_streak || 0} days</p>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-6">Study more to see your habit analysis</p>
          )}
        </CardContent>
      </Card>

      {/* Progress Over Time */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
            </div>
            Progress Over Time
          </CardTitle>
          <CardDescription>Your learning journey visualization</CardDescription>
        </CardHeader>
        <CardContent>
          {insights.progress_timeline && insights.progress_timeline.length > 0 ? (
            <div className="space-y-6">
              {/* Chart Visualization */}
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={insights.progress_timeline}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="period" 
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis 
                      domain={[0, 100]}
                      tick={{ fill: '#64748b', fontSize: 12 }}
                      axisLine={{ stroke: '#e2e8f0' }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1e293b', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value, name) => [`${value}%`, 'Score']}
                      labelStyle={{ color: '#94a3b8' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#6366f1" 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorScore)"
                      dot={{ fill: '#6366f1', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7, fill: '#4f46e5' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Weekly Breakdown */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {insights.progress_timeline.map((week, idx) => (
                  <div 
                    key={idx} 
                    className="bg-slate-50 rounded-xl p-4 text-center border border-slate-100"
                  >
                    <p className="text-xs text-slate-500 mb-1">{week.period}</p>
                    <p className="text-2xl font-bold text-slate-900">{week.score}%</p>
                    <div className={`flex items-center justify-center gap-1 text-xs mt-1 ${week.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {week.change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {week.change >= 0 ? '+' : ''}{week.change}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-slate-500 text-center py-6">Continue learning to see your progress over time</p>
          )}
        </CardContent>
      </Card>

      {/* AI Study Plan */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-white" />
                </div>
                Personalized Study Plan
              </CardTitle>
              <CardDescription>AI-generated study recommendations</CardDescription>
            </div>
            <Button 
              onClick={generateStudyPlan}
              disabled={generating}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Plan
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {insights.study_plan ? (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-yellow-500" />
                  FaheemGPT Recommendation
                </h4>
                <p className="text-slate-700 leading-relaxed whitespace-pre-line">{insights.study_plan.recommendation}</p>
              </div>
              
              {insights.study_plan.weekly_goals && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <h4 className="font-semibold text-slate-900 mb-3">Weekly Goals</h4>
                  <div className="space-y-2">
                    {insights.study_plan.weekly_goals.map((goal, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-slate-400 mt-0.5" />
                        <span className="text-slate-700">{goal}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Brain className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 mb-4">Get a personalized study plan from FaheemGPT</p>
              <Button 
                onClick={generateStudyPlan}
                disabled={generating}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {generating ? 'Generating...' : 'Generate Study Plan'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
